<template>
  <div>
    <SearchSpotifySong
      :title="title"
      :placeholder="placeholderInput"
      :loadingSelect="loading"
      :appName="appName"
      @remove-spotify-song-selected="removeSongSelected"
      @change-spotify-song-selected="selectSong"
    ></SearchSpotifySong>
    <div v-if="isShowMessage && !isSuccess" class="text-secondary">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import {
  EVENT_CHANGE_COLOR_QR_CODE,
  EVENT_CHANGE_SPOTIFY_SOUND_WAVE,
  QUERY_STRING_NO_CACHE,
} from '@/utils'
import useCustomizationStore from '@/store/customization'
import SearchSpotifySong from './SearchSpotifySong.vue'
import { useRoute } from 'vue-router'
import EventBus from '@/services/eventbus'
import { NullOrType } from '@/types'

const customizationStore = useCustomizationStore()
const route = useRoute()

interface Song {
  name: string
  artist: string
  image: string
  url: string
  uri: string
}

const props = withDefaults(
  defineProps<{
    appName?: string
    title: string
    placeholderInput: string
    qrCode?: boolean
    soundWave?: boolean
    qrCodeTemplate?: { title: string; values: string[] }
    selectImageButtonCustomily?: any
  }>(),
  {}
)

const emit = defineEmits<{
  (e: 'change-spotify-image-qrcode', image: string): void
  (e: 'change-spotify-song-selected', songSelected: Song): void
  (e: 'remove-spotify-song-selected'): void
}>()

const songSelected = ref<NullOrType<Song>>(null)
const loading = ref(false)
const imageQrCodeSelected = ref('')
const waveForm = ref([])
const colorQrCode = ref(props.qrCodeTemplate?.values[0] || '#000000')
const errorMessage = ref('Error!')
const isSuccess = ref(false)
const isShowMessage = ref(false)

onMounted(() => {
  EventBus.on(EVENT_CHANGE_COLOR_QR_CODE, (color) => {
    colorQrCode.value = color
    if (props.qrCode && songSelected.value && color) {
      loading.value = true
      isShowMessage.value = false
      isSuccess.value = false

      customizationStore
        .generateQrCodeByUri({
          uri: songSelected.value.uri,
          color,
          isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
        })
        .then((imageUr) => {
          emit('change-spotify-image-qrcode', imageUr)
          isSuccess.value = true
        })
        .catch((err) => {
          errorMessage.value = err.message ?? errorMessage.value
          isSuccess.value = false
        })
        .finally(() => {
          if (isSuccess.value) {
            customilyUpLoadingHandler()
          } else {
            loading.value = false
            isShowMessage.value = true
          }
        })
    }
  })
})

function genarateQrCodeAndWaveForm(song: Song) {
  if (!song) return
  const apis = []
  if (props.qrCode) {
    apis.push(
      customizationStore.generateQrCodeByUri({
        uri: song.uri,
        color: colorQrCode.value,
        isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
      })
    )
  }
  if (props.soundWave) {
    apis.push(
      customizationStore.generateWaveForm({
        type: 'url',
        url: song.uri,
        isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
      })
    )
  }

  loading.value = true
  isShowMessage.value = false
  isSuccess.value = false

  Promise.all(apis)
    .then((response: any) => {
      if (props.soundWave && props.qrCode) {
        imageQrCodeSelected.value = response[0]
        waveForm.value = response[1]
        emit('change-spotify-image-qrcode', response[0])
        EventBus.trigger(EVENT_CHANGE_SPOTIFY_SOUND_WAVE, response[1])
      } else if (props.qrCode) {
        imageQrCodeSelected.value = response[0]
        emit('change-spotify-image-qrcode', response[0])
      } else if (props.soundWave) {
        waveForm.value = response[0]
        EventBus.trigger(EVENT_CHANGE_SPOTIFY_SOUND_WAVE, response[0])
      }
      isSuccess.value = true
    })
    .catch((err) => {
      errorMessage.value = err.message ?? errorMessage.value
      isSuccess.value = false
    })
    .finally(() => {
      if (isSuccess.value) {
        customilyUpLoadingHandler()
      } else {
        loading.value = false
        isShowMessage.value = true
      }
    })
}

function selectSong(song: Song) {
  songSelected.value = song
  emit('change-spotify-song-selected', song)
  genarateQrCodeAndWaveForm(song)
}

function removeSongSelected() {
  songSelected.value = null
  emit('remove-spotify-song-selected')
}

function customilyUpLoadingHandler() {
  const timeout = setTimeout(() => {
    const isCustomilyUpLoading =
      props.selectImageButtonCustomily?.classList.contains('disabled')
    if (isCustomilyUpLoading) {
      customilyUpLoadingHandler()
    } else {
      loading.value = false
      isShowMessage.value = true
      clearTimeout(timeout)
    }
  }, 200)
}
</script>
